/**
 * Chargement des images de tableaux.
 *
 * Le code ne depend jamais du format : on tente les extensions dans l'ordre de
 * preference et on garde la premiere qui charge. Les placeholders SVG de dev et
 * les PNG/WebP de prod passent donc par le meme chemin, sans configuration.
 */
import type { Painting, PaintingsCatalog } from '@retusch/shared';
import catalogJson from '@retusch/content/paintings.json';

/** Du plus leger au plus universel. */
const EXTENSIONS = ['webp', 'png', 'jpg', 'svg'] as const;

export const CATALOG: PaintingsCatalog = catalogJson as PaintingsCatalog;

export function paintings(): readonly Painting[] {
  return CATALOG.paintings;
}

const cache = new Map<string, Promise<HTMLImageElement>>();

function tryLoad(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.addEventListener('load', () => resolve(image), { once: true });
    image.addEventListener('error', () => reject(new Error(`echec du chargement : ${url}`)), {
      once: true,
    });
    image.src = url;
  });
}

async function loadFirstAvailable(guid: string): Promise<HTMLImageElement> {
  const failures: string[] = [];
  for (const extension of EXTENSIONS) {
    const url = `/paintings/${guid}.${extension}`;
    try {
      return await tryLoad(url);
    } catch {
      failures.push(url);
    }
  }
  throw new Error(`aucune image trouvee pour ${guid} (essaye : ${failures.join(', ')})`);
}

export function loadArtwork(guid: string): Promise<HTMLImageElement> {
  const existing = cache.get(guid);
  if (existing !== undefined) return existing;

  const promise = loadFirstAvailable(guid);
  cache.set(guid, promise);
  // Un echec ne doit pas rester en cache : l'asset peut arriver plus tard.
  promise.catch(() => cache.delete(guid));
  return promise;
}

/**
 * Repli si le navigateur ne rapporte aucune taille naturelle : cas possible
 * avec un SVG sans width/height. Carre, comme les tableaux de prod : aucune
 * autre partie du code ne suppose de format, les zones etant normalisees.
 */
const FALLBACK_SIZE = 1024;

/** Taille naturelle de l'image, en pixels. */
export function naturalSize(image: HTMLImageElement): { width: number; height: number } {
  return {
    width: image.naturalWidth > 0 ? image.naturalWidth : FALLBACK_SIZE,
    height: image.naturalHeight > 0 ? image.naturalHeight : FALLBACK_SIZE,
  };
}
